import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { CloudDownloadOutlined } from '@material-ui/icons'

import { MODAL_TYPES, strings } from '@constants'
import { downloadFile } from '@data'
import { updateOrganisationApi } from '@data/conn/put/organisation'
import { fetchFinbudReport, getOrganisationCreditScoreAndPdf } from '@data/conn/get/finbud'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleModal } from '@data/state/action/root'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input } from '@baseV2'
import { Spacings, Text } from '@styles'

import { InputsWrapper } from '../styles'

const PAN_REGEX = /([A-Z]){5}([0-9]){4}([A-Z]){1}$/
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
// only a proprietorship PAN (4th char 'P') is eligible for an Mbook score
const PROPRIETOR_PAN_REGEX = /^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$/

const Divider = styled.hr`
	width: 100%;
	margin: 0;
	border: 0;
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`
const ScoreRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.MEDIUM};
	color: ${({ theme }) => theme.colors.text.secondary};

	b {
		color: ${({ theme }) => theme.colors.text.primary};
	}
`

const EditOrgPan = ({ orgDetails, update }) => {
	const dispatch = useDispatch()

	const isExport = orgDetails?.region === 'EXPORT'
	const panLabel = isExport ? strings(['organisation', 'registration']) : strings(['organisation', 'pan'])

	const [orgPan, setOrgPan] = useState(orgDetails?.pan ?? '')
	const [legalName, setLegalName] = useState(orgDetails?.gst_details?.[0]?.legal_name ?? '')
	const [creditScore, setCreditScore] = useState(null)
	const [creditReportUrl, setCreditReportUrl] = useState(null)

	// export orgs carry a registration number instead of a PAN, so any non-empty value is valid
	const isValidPanFormat = (pan) => (isExport ? Boolean(pan) : PROPRIETOR_PAN_REGEX.test(pan ?? ''))
	const canFetchScore = isValidPanFormat(orgPan) && !isExport

	const getOrgCreditScore = () => {
		dispatch(showLoader(true))
		getOrganisationCreditScoreAndPdf(orgDetails?.pan, orgDetails?.organisation_id)
			.then((res) => {
				setCreditScore(res?.credit_score ?? null)
				setCreditReportUrl(res?.credit_report_url ?? null)
			})
			.catch((err) => dispatch(showToast(true, err?.response?.data?.message, { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
	}

	useEffect(() => {
		setOrgPan(orgDetails?.pan ?? '')
		setLegalName(orgDetails?.gst_details?.[0]?.legal_name ?? '')
		if (isValidPanFormat(orgDetails?.pan) && !isExport) getOrgCreditScore()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orgDetails])

	const updateDisabled = () => {
		if (isExport) return !orgPan
		return !(PAN_REGEX.test(orgPan) || GST_REGEX.test(orgPan))
	}

	const onPanUpdate = () => {
		if (!orgPan) return
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: isExport ? strings(['confirm', 'to', 'update', 'registration']) : strings(['confirm', 'to', 'update', 'pan']),
				confirmAction: (actionType) => {
					dispatch(toggleModal(false))
					if (!actionType) return
					dispatch(showLoader(true))
					updateOrganisationApi(orgDetails?.organisation_id, 'PAN', orgPan)
						.then(() => {
							dispatch(showToast(true, strings('organisation_update_success'), { type: TOAST_TYPE.SUCCESS }))
							dispatch(toggleDrawer(false))
							update()
						})
						.catch((err) => {
							console.log(err)
							dispatch(showToast(true, strings('organisation_update_failure'), { type: TOAST_TYPE.ERROR }))
						})
						.finally(() => dispatch(showLoader(false)))
				},
			})
		)
	}

	const mbookScoreHandler = () => {
		if (!legalName || !orgPan) return
		dispatch(showLoader(true))
		fetchFinbudReport(legalName, orgPan, orgDetails?.organisation_id)
			.then(() => {
				dispatch(showToast(true, strings('msg_fetch_cibil_report_success'), { type: TOAST_TYPE.SUCCESS }))
				getOrgCreditScore()
				update()
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message || strings('msg_fetch_cibil_report_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Input
						id='org-pan-v2'
						label={panLabel}
						placeholder={isExport ? 'Enter registration number' : 'Enter PAN'}
						value={orgPan}
						maxLength={10}
						onChange={(e) => setOrgPan(isExport ? e.target.value.trim() : e.target.value.trim().toUpperCase())}
					/>

					{canFetchScore && (
						<>
							<Divider />
							<Input
								id='proprietor-name-v2'
								label='Proprietor Name'
								placeholder='Enter a proprietor name'
								value={legalName}
								onChange={(e) => setLegalName(e.target.value.trimStart())}
							/>
							<Button variant='outline' color='primary' size='small' disabled={!legalName} onClick={mbookScoreHandler}>
								{strings(['fetch', 'mbook', 'score'])}
							</Button>

							{creditScore && (
								<ScoreRow>
									<span>
										Mbook Score: <b>{creditScore}</b>
									</span>
									<Button
										type='button'
										variant='ghost'
										color='neutral'
										size='small'
										disabled={!creditReportUrl}
										aria-label='Download Mbook report'
										onClick={() => downloadFile(creditReportUrl, true)}
									>
										<CloudDownloadOutlined fontSize='small' />
									</Button>
								</ScoreRow>
							)}
						</>
					)}
				</InputsWrapper>
			</DrawerBodyWrapper>

			<DrawerFooterWrapper>
				<Button color='primary' disabled={updateDisabled()} onClick={onPanUpdate}>
					{strings('update')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default EditOrgPan
